import { useMemo } from 'react'
import { trpc } from '#app/lib/trpc-tanstack.js'
import { useSerialQueries } from '#app/utils/serial-query.js'

type SmartSearchEndpoint = 'smart' | 'smartPublic'

interface UseIconSetSmartSearchOptions {
	/** IDs for 'smart' endpoint, slugs for 'smartPublic' endpoint */
	identifiers: string[]
	query: string
	shouldRunSmart: boolean
	endpoint: SmartSearchEndpoint
}

export function useIconSetSmartSearch({
	identifiers,
	query,
	shouldRunSmart,
	endpoint,
}: UseIconSetSmartSearchOptions) {
	const smartQueriesResults = useSerialQueries({
		queries: identifiers.map((id) => {
			const options =
				endpoint === 'smartPublic'
					? trpc.search.smartPublic.queryOptions({ query, slug: id })
					: trpc.search.smart.queryOptions({ query, uploadedId: id })
			return { ...options, enabled: shouldRunSmart }
		}),
		serialKey: 'smartSearch',
	})

	// Build combined results for IconGridBySearch
	const smartResults = useMemo(() => {
		if (!shouldRunSmart) return undefined
		const results: Record<string, string[]> = {}
		smartQueriesResults.forEach((result, index) => {
			const identifier = identifiers[index]
			if (result.data && identifier) results[identifier] = result.data
		})
		return Object.keys(results).length > 0 ? results : undefined
	}, [smartQueriesResults, identifiers, shouldRunSmart])

	const hasMoreSmartResults =
		shouldRunSmart && smartQueriesResults.some((r) => r.isPending)

	return {
		smartResults,
		hasMoreSmartResults,
	}
}
